import { useState } from 'react'

import {
  Box,
  Button,
  Chevron,
  CollapsableCard,
  Icon,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  AtsCancelMessageDocument,
  AtsCancelMessageMutation,
  AtsConversationMessageFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { CalendarInfo, formatBackendDateTimeToCalendar } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CancelConfirmationModal } from './CancelConfirmationModal'

interface Props {
  message: AtsConversationMessageFragment
}

const delayedMessageSendText = (sentByMe: boolean, calendarInfo: CalendarInfo) => {
  const when = calendarInfo.calendar
  switch (calendarInfo.calendarFormat) {
    case 'sameDay':
      return sentByMe
        ? i18n.t('ats.candidates.messages.new_message.delayed_send.by_me_same_day', { when })
        : i18n.t('ats.candidates.messages.new_message.delayed_send.by_other_same_day', { when })
    case 'nextDay':
      return sentByMe
        ? i18n.t('ats.candidates.messages.new_message.delayed_send.by_me_next_day', { when })
        : i18n.t('ats.candidates.messages.new_message.delayed_send.by_other_next_day', { when })

    case 'nextWeek':
      return sentByMe
        ? i18n.t('ats.candidates.messages.new_message.delayed_send.by_me_next_week', { when })
        : i18n.t('ats.candidates.messages.new_message.delayed_send.by_other_next_week', { when })

    default:
      return sentByMe
        ? i18n.t('ats.candidates.messages.new_message.delayed_send.by_me_else', { when })
        : i18n.t('ats.candidates.messages.new_message.delayed_send.by_other_else', { when })
  }
}

export const DelayedMessageCard = ({ message }: Props) => {
  const [showDelayedMessage, setShowDelayedMessage] = useState(false)
  const [showConfirmationModal, setConfirmationModal] = useState(false)
  const [cancelAtsMessageMutation, { loading: cancelling }] = useMutation(
    AtsCancelMessageDocument,
    {
      extractorFn: (data: AtsCancelMessageMutation) => data.atsMutations.cancelAtsMessage,
    }
  )
  const {
    access: { userId: userId },
  } = useCurrentSession()

  const sentByMe = message.sentById === userId

  if (!message.delayedUntil) return null

  const calendarInfo = formatBackendDateTimeToCalendar(message.delayedUntil)

  const openCancelConfirmationModal = (event: React.SyntheticEvent) => {
    if (cancelling) return

    setConfirmationModal(true)
    event?.stopPropagation()
  }

  const onCancel = async () => {
    const result = await cancelAtsMessageMutation({
      variables: { id: message.id },
      update(cache) {
        cache.evict({ id: cache.identify(message) })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }
  }

  return (
    <>
      <CollapsableCard
        open={showDelayedMessage}
        contentBackground="grey100"
        Card={
          <Box width="full" flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            <Touchable onClick={() => setShowDelayedMessage(!showDelayedMessage)}>
              <Box flexDirection="row" alignItems="center" gap="s8" width="fit-content">
                <Text.Normal size="300" color="grey1000" weight="medium">
                  {delayedMessageSendText(sentByMe, calendarInfo)}
                </Text.Normal>
                <Chevron type="primary" direction={showDelayedMessage ? 'up' : 'down'} />
              </Box>
            </Touchable>
            <Button
              loading={cancelling}
              type="secondary"
              onClick={openCancelConfirmationModal}
              iconName="delete"
            />
          </Box>
        }
        Content={
          <Box width="full">
            <Box flexDirection="column" gap="s16">
              <RichTextDisplay content={message.content} />

              {message.attachments.length > 0 && (
                <Box flexDirection="row" gap="s12">
                  {message.attachments.map((attachment, key) => (
                    <Box
                      key={`${attachment.filename}-${key}`}
                      flexDirection="row"
                      alignItems="center"
                      gap="s8"
                    >
                      <Icon.Medium icon="paperclip" color="grey1000" />
                      <Text.Normal size="200" color="grey1000">
                        {attachment.filename}
                      </Text.Normal>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        }
      />

      {showConfirmationModal && (
        <CancelConfirmationModal onClose={() => setConfirmationModal(false)} onSubmit={onCancel} />
      )}
    </>
  )
}
